type EnvMap = Record<string, string>

declare const __APP_ENV__: EnvMap | undefined

const readFromProcess = (key: string): string | undefined => {
  if (typeof process === 'undefined' || typeof process.env === 'undefined') {
    return undefined
  }

  const value = process.env[key]

  if (typeof value === 'string' && value.length > 0) {
    return value
  }

  return undefined
}

const readFromShim = (key: string): string | undefined => {
  if (typeof __APP_ENV__ !== 'undefined' && __APP_ENV__ && key in __APP_ENV__) {
    const value = __APP_ENV__[key]
    if (typeof value === 'string' && value.length > 0) {
      return value
    }
  }

  if (typeof globalThis !== 'undefined') {
    const shim = (globalThis as { __APP_ENV__?: EnvMap }).__APP_ENV__
    if (shim && key in shim) {
      const value = shim[key]
      if (typeof value === 'string' && value.length > 0) {
        return value
      }
    }
  }

  return undefined
}

const buildCandidates = (name: string): string[] => {
  const normalized = name.toUpperCase()

  return [
    name,
    normalized,
    `REACT_APP_${normalized}`,
    `VITE_${normalized}`,
  ]
}

const normalizeBaseValue = (raw: string): string => {
  const trimmed = raw.trim()
  if (!trimmed || trimmed === '/' || trimmed === './') {
    return ''
  }

  try {
    const url = new URL(trimmed, 'http://placeholder')
    const pathname = url.pathname || ''
    if (!pathname || pathname === '/') {
      return ''
    }
    return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  } catch {
    const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
    const cleaned = withLeadingSlash.replace(/\/+$/, '')
    return cleaned === '/' ? '' : cleaned
  }
}

export const getEnvVar = (name: string): string | undefined => {
  const candidates = buildCandidates(name)

  for (const candidate of candidates) {
    const fromProcess = readFromProcess(candidate)
    if (typeof fromProcess !== 'undefined') {
      return fromProcess
    }

    const fromShim = readFromShim(candidate)
    if (typeof fromShim !== 'undefined') {
      return fromShim
    }
  }

  return undefined
}

export const requireEnvVar = (name: string): string => {
  const value = getEnvVar(name)

  if (typeof value === 'undefined') {
    throw new Error(`Missing environment variable: ${name}`)
  }

  return value
}

export const getBasePath = (): string => {
  const raw =
    getEnvVar('PUBLIC_URL') ??
    getEnvVar('BASE_URL') ??
    ''

  return normalizeBaseValue(raw)
}

export const withBasePath = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const base = getBasePath()

  if (!base) {
    return normalizedPath
  }

  return `${base}${normalizedPath}`
}
